import { Component, Input, OnInit } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';
import { forkJoin } from 'rxjs';
import { ClientService } from 'src/app/core/services/client.service';
import { PresetService } from 'src/app/core/services/preset.service';

interface TargetItem {
  id: string;
  position: number;
  usePremium: boolean;
  hasConfig: boolean;
  thumbnailPath: string | null;
}

interface PresetItem {
  id: string;
  name: string;
  targets: TargetItem[];
  expanded: boolean;
}

@Component({
  selector: 'app-premium-targets-popup',
  templateUrl: './premium-targets-popup.component.html',
  styleUrls: ['./premium-targets-popup.component.css'],
})
export class PremiumTargetsPopupComponent implements OnInit {
  @Input() clientId: string;
  @Input() isPremiumEnabled: boolean = false;

  presets: PresetItem[] = [];
  filteredPresets: PresetItem[] = [];
  presetSearchQuery: string = '';
  selectedPreset: PresetItem | null = null;
  originalTargetStates: Map<string, boolean> = new Map();
  isLoading: boolean = true;
  isSaving: boolean = false;

  constructor(
    public activeModal: NgbActiveModal,
    private presetService: PresetService,
    private clientService: ClientService,
    private toastr: ToastrService,
    private translate: TranslateService
  ) {}

  ngOnInit(): void {
    if (this.isPremiumEnabled) {
      this.loadPresets();
    } else {
      this.isLoading = false;
    }
  }

  loadPresets(): void {
    this.isLoading = true;
    this.presetService.getClientPresets(this.clientId).subscribe(
      (clientPresets) => {
        this.presets = clientPresets
          .map((preset: any) => ({
            id: preset.id,
            name: preset.name,
            targets: (preset.targets || [])
              .map((target) => ({
                id: target.id,
                position: target.position,
                usePremium: target.clientPresetTargetConfig?.usePremium || false,
                hasConfig: !!target.clientPresetTargetConfig,
                thumbnailPath: target.tutorialFile?.fileInfo?.thumbnailPath || null,
              }))
              .sort((a, b) => a.position - b.position),
            expanded: false,
          }))
          .sort((a, b) => a.name.localeCompare(b.name));
        this.filteredPresets = [...this.presets];

        this.presets.forEach((preset) => {
          preset.targets.forEach((target) => {
            this.originalTargetStates.set(target.id, target.usePremium);
          });
        });

        this.isLoading = false;
      },
      () => {
        this.isLoading = false;
        this.toastr.error('Error loading presets');
      }
    );
  }

  filterPresets(): void {
    const query = this.presetSearchQuery.toLowerCase().trim();
    if (!query) {
      this.filteredPresets = [...this.presets];
    } else {
      this.filteredPresets = this.presets.filter((preset) =>
        preset.name.toLowerCase().includes(query)
      );
    }
  }

  selectPreset(preset: PresetItem): void {
    // Restore previous preset to original state if switching
    if (this.selectedPreset && this.selectedPreset.id !== preset.id) {
      this.restoreOriginalState(this.selectedPreset);
    }

    if (this.selectedPreset?.id === preset.id) {
      this.restoreOriginalState(this.selectedPreset);
      this.selectedPreset = null;
    } else {
      this.selectedPreset = preset;
    }
  }

  private updateOriginalState(preset: PresetItem): void {
    preset.targets.forEach((target) => {
      this.originalTargetStates.set(target.id, target.usePremium);
    });
  }

  private restoreOriginalState(preset: PresetItem): void {
    preset.targets.forEach((target) => {
      const originalValue = this.originalTargetStates.get(target.id);
      if (originalValue !== undefined) {
        target.usePremium = originalValue;
      }
    });
  }

  toggleTargetPremium(target: TargetItem): void {
    target.usePremium = !target.usePremium;
  }

  get areAllSelected(): boolean {
    if (!this.selectedPreset || this.selectedPreset.targets.length === 0) {
      return false;
    }
    return this.selectedPreset.targets.every((target) => target.usePremium);
  }

  toggleAll(): void {
    if (this.selectedPreset) {
      const newValue = !this.areAllSelected;
      this.selectedPreset.targets.forEach((target) => {
        target.usePremium = newValue;
      });
    }
  }

  save(): void {
    if (!this.selectedPreset) {
      return;
    }

    this.isSaving = true;

    const requests = this.selectedPreset.targets.map((target) => {
      const body = { usePremium: target.usePremium };
      return this.clientService.updatePresetTargetConfig(
        this.clientId,
        target.id,
        body
      );
    });

    if (requests.length === 0) {
      this.isSaving = false;
      return;
    }

    forkJoin(requests).subscribe(
      () => {
        this.isSaving = false;
        this.toastr.success('Premium settings saved');
        // Update original state after successful save
        if (this.selectedPreset) {
          this.updateOriginalState(this.selectedPreset);
        }
      },
      () => {
        this.isSaving = false;
        this.toastr.error('Error saving premium settings');
      }
    );
  }

  close(): void {
    this.activeModal.dismiss();
  }
}
